/**
 * WHAT THE DISPATCHER SCREEN ON SCREEN IS ABOUT — a one-value channel from the
 * screen inside the frame to the assistant launcher in it.
 *
 * WHY THE SCREENS PUSH AND THE FRAME LISTENS. The launcher lives in
 * DispatcherLayout; the thing worth quoting belongs to the screen inside it.
 * `useLocation` would tell the frame it is on `/portal/dispatcher/trip/:id`,
 * but what is in that URL is a mongo ObjectId — not a booking reference, not
 * something to show a dispatcher, and not something to put in a prompt. The
 * REFERENCE only exists once the trip has been read, and the screen is what
 * reads it. The board's period is likewise the range an answer came back for,
 * not the one the query string asked for. So each screen declares its own
 * subject for as long as it is mounted, and the frame holds the latest one.
 *
 * ITS OWN MODULE, not part of DispatcherAssistant, so that Board and
 * TripDetails can publish without importing the chat agent, its service and
 * the sheet along with it.
 */
import React, {
  createContext, useContext, useEffect, useState,
} from 'react';

const AskSubjectContext = createContext(null);

/** Held by the frame: the current subject, and the setter the screens get. */
export function useAskSubjectState() {
  const [subject, setSubject] = useState(null);
  return { subject, setSubject };
}

/** The frame wraps its children in this, so any screen under it can publish. */
export function AskSubjectProvider({ setSubject, children }) {
  return (
    <AskSubjectContext.Provider value={setSubject}>{children}</AskSubjectContext.Provider>
  );
}

/**
 * Declare what THIS screen is about, for as long as it is on screen.
 *
 * `kind` is 'trip' or 'board'; `value` is a booking REFERENCE or the period's
 * heading. Both primitives, so the effect does not re-run every render the way
 * an object literal would. A missing value publishes nothing at all, which is
 * the right answer while a trip is still loading.
 */
export default function useAskSubject(kind, value) {
  const publish = useContext(AskSubjectContext);

  useEffect(() => {
    if (!publish || !kind || !value) return undefined;

    const mine = { kind, value };
    publish(mine);
    // Clear only what THIS screen put there. Going board → trip can mount the
    // arriving screen before the leaving one has run its cleanup, and a blind
    // `publish(null)` would wipe the subject the new screen had just set.
    return () => publish((current) => (current === mine ? null : current));
  }, [publish, kind, value]);
}
